// Desktop-only glue between the main window and the Pay-Assistant tray window.
// The main window owns the session (and the rotating refresh token); the
// assistant window borrows short-lived access tokens over Tauri events.
import { invoke } from '@tauri-apps/api/core';
import { emit, listen } from '@tauri-apps/api/event';
import { writeText } from '@tauri-apps/plugin-clipboard-manager';
import { refreshSession, setRefresher, tokens } from '../api/client';
import { session } from '../api/auth';
import { isDesktop } from '../platform';

export const EV = {
  token: 'rimon:token',
  tokenRequest: 'rimon:token-request',
  /** Emitted by the Rust key watcher when ⌘V / Ctrl+V is pressed in any app. */
  pasted: 'pa:pasted',
  /** ⌘C / Ctrl+C after everything is pasted → book the payment. */
  confirm: 'pa:confirm',
  /** A payment was booked in one window → the other reloads. */
  paid: 'pa:paid',
  /** Main window asks the assistant to (re)load its queue. */
  reload: 'pa:reload',
  /** The Pay-Assistant's one on/off state changed (bool; Rust `ACTIVE`: app switch, tray icon, ⌘⌥P, popover switch). */
  active: 'pa:active',
  /** Main window: session user replaced (`changed`, e.g. new rights from RimoID) or gone (`lost`). */
  session: 'rimon:session',
} as const;

/** Main window: answer token requests and push every new token. */
export function serveSession(): () => void {
  if (!isDesktop()) return () => {};
  const offToken = tokens.onChange((t) => void emit(EV.token, t));
  // The assistant has no socket and no refresh of its own: it follows the main window's session.
  const offUser = session.subscribe((u) => void emit(EV.session, u ? 'changed' : 'lost'));
  const pending = listen<{ refresh?: boolean }>(EV.tokenRequest, async (e) => {
    if (e.payload?.refresh || !tokens.access) await refreshSession();
    await emit(EV.token, tokens.access);
  });
  return () => {
    offToken();
    offUser();
    void pending.then((un) => un());
  };
}

/** Assistant window: get an access token from the main window. */
export function borrowToken(refresh = false): Promise<string | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (t: string | null) => {
      if (done) return;
      done = true;
      resolve(t);
    };
    void listen<string | null>(EV.token, (e) => {
      tokens.set(e.payload);
      finish(e.payload);
    }).then(() => emit(EV.tokenRequest, { refresh }));
    setTimeout(() => finish(null), 4000);
  });
}

/** Assistant window: 401 → ask the main window instead of refreshing locally. */
export function borrowMainSession(): void {
  setRefresher(async () => !!(await borrowToken(true)));
}

/** Clipboard write that also works while another app is focused. */
export async function copyText(text: string): Promise<void> {
  try {
    await writeText(text);
  } catch {
    await navigator.clipboard?.writeText(text).catch(() => {});
  }
}

/** Switches the Pay-Assistant on/off for every window (the window and tray text follow). */
export const paSetActive = (on: boolean) => (isDesktop() ? invoke('pa_set_active', { on }).catch(() => {}) : Promise.resolve());
export const paActive = () => (isDesktop() ? invoke<boolean>('pa_active').catch(() => false) : Promise.resolve(false));
export const paShow = () => (isDesktop() ? invoke('pa_show').catch(() => {}) : Promise.resolve());
export const paHide = () => (isDesktop() ? invoke('pa_hide').catch(() => {}) : Promise.resolve());
export const paResize = (height: number, width?: number) => invoke('pa_resize', { height, width }).catch(() => {});
export const paPin = (pinned: boolean) => invoke('pa_pin', { pinned }).catch(() => {});
export const paDrag = () => invoke('pa_drag').catch(() => {});
export const paMove = (x: number, y: number) => invoke('pa_move', { x, y }).catch(() => {});
export const paPosition = () => invoke<[number, number]>('pa_position').catch(() => null);
export const paStatus = (text: string) => invoke('pa_status', { text }).catch(() => {});
